import React, { useEffect, useState } from "react";
import { Grid, Typography } from "../../../common/utils/mui-imports";
import StatusCard from "./StatusCard";
import {
  InspectionAssignmentOverviewDTO,
  InspectionDataDTO,
} from "../../../common/types/common.dto.types";
import noData from "../../../images/no_data.svg";

interface GridLayoutProps {
  inspectionLocations: any[];
  inspectionYears: number[];
}

const GridLayout: React.FC<GridLayoutProps> = ({
  inspectionLocations,
  inspectionYears,
}) => {
  const [filteredLocations, setFilteredLocations] = useState<any[]>([]);
  /**
   * @name isAllYearsWithoutData
   * @description Checks if all years have no data
   * @param inspectionsByYear
   * @returns boolean
   */
  const isAllYearsWithoutData = (
    inspectionsByYear: { [year: string]: InspectionDataDTO[] } | undefined
  ): boolean => {
    if (!inspectionsByYear || Object.keys(inspectionsByYear).length === 0) {
      return true;
    }

    return inspectionYears.every((year) => {
      const yearKey = String(year);
      const inspections = inspectionsByYear[yearKey];
      return !Array.isArray(inspections) || inspections.length === 0;
    });
  };

  /**
   * @description Filter locations without any inspections for all years
   */
  useEffect(() => {
    const filteredList = inspectionLocations.filter(
      (item) => !isAllYearsWithoutData(item.InspectionsByYear)
    );

    setFilteredLocations(filteredList);
  }, [inspectionLocations]);

  return (
    <div
      className="inspection-timeline-container"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {filteredLocations.length > 0 ? (
        <>
          {/* Header - stays sticky at the top */}
          <div style={{ flexShrink: 0 }}>
            <Grid
              container
              className="mb-5 inspection-grid-table-header"
              sx={{
                display: {
                  xs: "none",
                  md: "flex",
                },
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 10,
                paddingY: 1,
              }}
              spacing={2}
            >
              <Grid item xs={12} md={2}>
                <div style={{ textAlign: "left" }}>Location(s)</div>
              </Grid>

              {inspectionYears?.map((year, index) => (
                <Grid item xs={12} md={3.33} key={year}>
                  <div style={{ textAlign: "left" }}>{year}</div>
                </Grid>
              ))}
            </Grid>
          </div>

          {/* Content - scrollable area */}
          <div
            style={{
              flexGrow: 1,
              overflowY: "auto",
              paddingBottom: "20px",
            }}
          >
            {/* Location Rows */}
            {filteredLocations?.map(
              (locationRow: InspectionAssignmentOverviewDTO, rowIndex) => (
                <React.Fragment key={locationRow.SiteId}>
                  <Grid container spacing={2} className="location-row">
                    <Grid item xs={12} md={2}>
                      <div className="location-column-wrapper">
                        <Typography
                          variant="subtitle1"
                          className="label-style"
                          sx={{ display: { xs: "block", md: "none" } }}
                        >
                          Location(s)
                        </Typography>
                        <div
                          style={{
                            minHeight: "200px",
                          }}
                          className="location-column"
                        >
                          {locationRow.SiteName}
                        </div>
                      </div>
                    </Grid>

                    {inspectionYears?.map((year) => (
                      <Grid key={year} item xs={12} md={3.33}>
                        <div className="year-column-wrapper">
                          <Typography
                            variant="subtitle1"
                            className="label-style"
                            sx={{ display: { xs: "block", md: "none" } }}
                          >
                            {year}
                          </Typography>
                          <div className="year-column">
                            {locationRow.InspectionsByYear?.[year]?.length >
                            0 ? (
                              locationRow.InspectionsByYear[year].map(
                                (inspection, index) => (
                                  <StatusCard
                                    key={inspection.Id}
                                    title={inspection.Name}
                                    date={inspection.PlannedCompletionDate}
                                    statusId={inspection.InspectionStatusId}
                                    assessmentType={
                                      inspection.AssessmentTypeName
                                    }
                                  />
                                )
                              )
                            ) : (
                              <div className="gray-text body2 flex__item--vcenter nodata-text">
                                No records found
                              </div>
                            )}
                          </div>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                </React.Fragment>
              )
            )}
          </div>
        </>
      ) : (
        <div className="no-data width100-p flex--container">
          <img className="" src={noData} alt="" style={{}}></img>
          <div className="gray-text body2 flex__item--vcenter nodata-text">
            No records found
          </div>
        </div>
      )}
    </div>
  );
};

export default GridLayout;
